<template>
  <div class="design-system-page">
    <header class="ds-intro">
      <p class="ds-eyebrow">Review only · not in main nav</p>
      <h1>BHMLF Design System</h1>
      <p class="ds-lead">
        Current measured styles (left) versus recommended Black Hills / wildlife
        tokens (right). Full write-up:
        <code>bhmtnlionfoundation-design-system.md</code>
      </p>
    </header>

    <!-- Colors -->
    <section class="ds-section">
      <h2>Color</h2>
      <div class="ds-compare">
        <div class="ds-col ds-current">
          <h3>Current</h3>
          <div class="swatch-grid">
            <div
              v-for="swatch in currentColors"
              :key="swatch.hex"
              class="swatch"
            >
              <div class="swatch-chip" :style="{ background: swatch.hex }" />
              <div class="swatch-meta">
                <strong>{{ swatch.name }}</strong>
                <code>{{ swatch.hex }}</code>
                <span>{{ swatch.use }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="ds-col ds-recommended">
          <h3>Recommended</h3>
          <div class="swatch-grid">
            <div
              v-for="swatch in recommendedColors"
              :key="swatch.hex"
              class="swatch"
            >
              <div class="swatch-chip" :style="{ background: swatch.hex }" />
              <div class="swatch-meta">
                <strong>{{ swatch.name }}</strong>
                <code>{{ swatch.hex }}</code>
                <span>{{ swatch.use }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Typography -->
    <section class="ds-section">
      <h2>Typography</h2>
      <div class="ds-compare">
        <div class="ds-col ds-current">
          <h3>Current</h3>
          <p class="type-note">
            Mix of Avenir / Arial / Roboto depending on page
          </p>
          <p class="type-current-h1">Welcome heading · 40px / 700</p>
          <p class="type-current-h2">Section heading · 32px / 700</p>
          <p class="type-current-h3">Event title · 24px / 700</p>
          <p class="type-current-body">
            Body copy at 16px / 400 / line-height 1.6 on Arial pages; global
            #app uses Avenir at 16px / 24px line-height and color #2c3e50.
          </p>
          <p class="type-current-caption">Caption / footer · ~12.8–14.4px</p>
        </div>
        <div class="ds-col ds-recommended">
          <h3>Recommended</h3>
          <p class="type-note">Outfit + Source Sans 3</p>
          <p class="type-rec-h1">Welcome heading · H1</p>
          <p class="type-rec-h2">Section heading · H2</p>
          <p class="type-rec-h3">Event title · H3</p>
          <p class="type-rec-body">
            Body copy uses Source Sans 3 at 16px with 1.6 line-height and ink
            #1A1F1C for readable long-form mission and event content.
          </p>
          <p class="type-rec-caption">Caption / label · 13px / 500</p>
        </div>
      </div>
    </section>

    <!-- Buttons -->
    <section class="ds-section">
      <h2>Buttons &amp; CTAs</h2>
      <div class="ds-compare">
        <div class="ds-col ds-current">
          <h3>Current (three systems)</h3>
          <div class="btn-row">
            <button type="button" class="btn-current-orange">
              Learn About Sponsorship
            </button>
            <a class="btn-current-green" href="#0" @click.prevent>Contact Us</a>
            <button type="button" class="btn-current-vuetify">
              Pre-register on Eventbrite
            </button>
            <button type="button" class="btn-current-text">Donate</button>
          </div>
          <ul class="ds-list">
            <li>Orange HTML button · #ff9800 · radius 5px</li>
            <li>Vue-green link · #42b983 · radius 5px</li>
            <li>Vuetify primary · #1867c0 · uppercase · radius 4px</li>
            <li>Donate text · blue #1867c0</li>
          </ul>
        </div>
        <div class="ds-col ds-recommended">
          <h3>Recommended</h3>
          <div class="btn-row">
            <button type="button" class="btn-rec-primary">Donate</button>
            <button type="button" class="btn-rec-secondary">
              Learn about sponsorship
            </button>
            <button type="button" class="btn-rec-tertiary">Contact</button>
          </div>
          <ul class="ds-list">
            <li>Primary amber · donate / high priority</li>
            <li>Secondary forest · events &amp; sponsor</li>
            <li>Tertiary text · low emphasis / nav</li>
          </ul>
        </div>
      </div>
    </section>

    <!-- Forms -->
    <section class="ds-section">
      <h2>Forms &amp; inputs</h2>
      <div class="ds-compare">
        <div class="ds-col ds-current">
          <h3>Current</h3>
          <p class="type-note">
            Live contact form is commented out; mailto uses browser blue
            #0000ee. Sample field uses Material-like chrome.
          </p>
          <label class="field-label-current">Your name</label>
          <input class="field-current" type="text" value="Sample input" />
          <a class="link-current-browser" href="#0" @click.prevent>
            blackhillsmountainlionhunters@gmail.com
          </a>
        </div>
        <div class="ds-col ds-recommended">
          <h3>Recommended</h3>
          <label class="field-label-rec">Your name</label>
          <input class="field-rec" type="text" value="Sample input" />
          <a class="link-rec" href="#0" @click.prevent>
            blackhillsmountainlionhunters@gmail.com
          </a>
        </div>
      </div>
    </section>

    <!-- Cards -->
    <section class="ds-section">
      <h2>Cards &amp; spacing</h2>
      <div class="ds-compare">
        <div class="ds-col ds-current">
          <h3>Current</h3>
          <div class="card-current-event">
            <h4>Featured event card</h4>
            <p>
              Radius 8px · left border #42b983 · fill #f9f9f9 · max-width 600px
              · shadow 0 2px 4px rgba(0,0,0,0.1)
            </p>
          </div>
          <div class="card-current-vuetify">
            <h4>Vuetify card</h4>
            <p>Radius 4px · elevation-1 Material shadow · white fill</p>
          </div>
        </div>
        <div class="ds-col ds-recommended">
          <h3>Recommended</h3>
          <div class="card-rec card-rec-featured">
            <h4>Featured event card</h4>
            <p>
              Radius 8px · forest accent border · light stone-friendly surface
              · subtle shadow
            </p>
          </div>
          <div class="card-rec">
            <h4>Standard card</h4>
            <p>Same radius family · 1px border · no competing elevations</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Nav / footer notes -->
    <section class="ds-section">
      <h2>Navigation &amp; footer</h2>
      <div class="ds-compare">
        <div class="ds-col ds-current">
          <h3>Current patterns</h3>
          <div class="nav-mock-current">
            <span class="nav-logo">Logo 50px</span>
            <span>Home</span>
            <span>Sponsor</span>
            <span>Events</span>
            <span class="nav-donate-blue">DONATE</span>
            <span>Contact</span>
          </div>
          <div class="footer-mock-current">
            <strong>Footer #2c3e50</strong>
            <p>Avenir stack · muted #bdc3c7 links · soft-white email</p>
          </div>
        </div>
        <div class="ds-col ds-recommended">
          <h3>Recommended patterns</h3>
          <div class="nav-mock-rec">
            <span class="nav-logo-rec">BHMLF</span>
            <span>Home</span>
            <span>Sponsor</span>
            <span>Events</span>
            <span class="nav-donate-amber">Donate</span>
            <span>Contact</span>
          </div>
          <div class="footer-mock-rec">
            <strong>Footer #243447</strong>
            <p>Same type stack as body · amber donate · forest-tinted muted links</p>
          </div>
        </div>
      </div>
    </section>

    <footer class="ds-footer-note">
      <p>
        Values measured via Playwright <code>getComputedStyle</code> on local
        preview. Implementing recommendations site-wide is a separate follow-up.
      </p>
    </footer>
  </div>
</template>

<script>
export default {
  name: "DesignSystemPage",
  data() {
    return {
      currentColors: [
        { name: "Slate / footer", hex: "#2c3e50", use: "App text, footer bg" },
        { name: "Body (scoped)", hex: "#333333", use: "Home / content pages" },
        { name: "Vuetify primary", hex: "#1867c0", use: "v-btn primary" },
        { name: "Vue green CTA", hex: "#42b983", use: "Sponsor link, accents" },
        { name: "Link green", hex: "#105838", use: "Mission router-link" },
        { name: "Orange CTA", hex: "#ff9800", use: "Home sponsor button" },
        { name: "Event fill", hex: "#f9f9f9", use: "Event cards" },
        { name: "Footer muted", hex: "#bdc3c7", use: "Footer copy / links" },
      ],
      recommendedColors: [
        { name: "Forest", hex: "#1E3A2F", use: "Brand / secondary CTA" },
        { name: "Amber", hex: "#C17A2F", use: "Primary donate CTA" },
        { name: "Slate", hex: "#243447", use: "Footer / dark UI" },
        { name: "Ink", hex: "#1A1F1C", use: "Body text" },
        { name: "Muted", hex: "#5C6560", use: "Captions" },
        { name: "Stone", hex: "#F3F5F2", use: "Page background" },
        { name: "Border", hex: "#C9D0CB", use: "Inputs / cards" },
        { name: "Focus", hex: "#2F6B52", use: "Focus rings" },
      ],
    };
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Outfit:wght@500;600&family=Source+Sans+3:wght@400;500;600&display=swap");

.design-system-page {
  --color-forest: #1e3a2f;
  --color-forest-hover: #152a22;
  --color-amber: #c17a2f;
  --color-amber-hover: #a36626;
  --color-slate: #243447;
  --color-ink: #1a1f1c;
  --color-muted: #5c6560;
  --color-stone: #f3f5f2;
  --color-surface: #ffffff;
  --color-border: #c9d0cb;
  --color-focus: #2f6b52;
  --font-display: "Outfit", system-ui, sans-serif;
  --font-body: "Source Sans 3", system-ui, sans-serif;

  max-width: 1120px;
  margin: 0 auto;
  padding: 40px 20px 80px;
  text-align: left;
  color: #1a1f1c;
  font-family: var(--font-body);
  background: linear-gradient(180deg, #f3f5f2 0%, #ffffff 120px);
}

.ds-intro {
  margin-bottom: 48px;
}

.ds-eyebrow {
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--color-muted);
  margin: 0 0 8px;
}

.ds-intro h1 {
  font-family: var(--font-display);
  font-size: 2.5rem;
  font-weight: 600;
  line-height: 1.15;
  margin: 0 0 12px;
  color: var(--color-forest);
}

.ds-lead {
  font-size: 1.125rem;
  line-height: 1.55;
  color: var(--color-muted);
  max-width: 680px;
  margin: 0;
}

.ds-section {
  margin-bottom: 56px;
}

.ds-section > h2 {
  font-family: var(--font-display);
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 20px;
  color: var(--color-forest);
  border-bottom: 1px solid var(--color-border);
  padding-bottom: 8px;
}

.ds-compare {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

@media (max-width: 900px) {
  .ds-compare {
    grid-template-columns: 1fr;
  }
}

.ds-col {
  background: #fff;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 20px;
}

.ds-col h3 {
  font-family: var(--font-display);
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 16px;
}

.ds-current h3 {
  color: #2c3e50;
}

.ds-recommended h3 {
  color: var(--color-forest);
}

.swatch-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.swatch {
  display: flex;
  gap: 12px;
  align-items: center;
}

.swatch-chip {
  width: 48px;
  height: 48px;
  border-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  flex-shrink: 0;
}

.swatch-meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 13px;
}

.swatch-meta code {
  font-size: 12px;
}

.swatch-meta span {
  color: var(--color-muted);
}

.type-note {
  font-size: 13px;
  color: var(--color-muted);
  margin: 0 0 16px;
}

.type-current-h1 {
  font-family: Arial, sans-serif;
  font-size: 40px;
  font-weight: 700;
  line-height: 1.15;
  margin: 0 0 8px;
  color: #333;
}

.type-current-h2 {
  font-family: Arial, sans-serif;
  font-size: 32px;
  font-weight: 700;
  margin: 0 0 8px;
  color: #333;
}

.type-current-h3 {
  font-family: Arial, sans-serif;
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 8px;
  color: #2c3e50;
}

.type-current-body {
  font-family: Arial, sans-serif;
  font-size: 16px;
  line-height: 1.6;
  color: #333;
  margin: 0 0 8px;
}

.type-current-caption {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  font-size: 12.8px;
  color: #95a5a6;
  margin: 0;
}

.type-rec-h1 {
  font-family: var(--font-display);
  font-size: 2.5rem;
  font-weight: 600;
  line-height: 1.15;
  margin: 0 0 8px;
  color: var(--color-ink);
}

.type-rec-h2 {
  font-family: var(--font-display);
  font-size: 2rem;
  font-weight: 600;
  line-height: 1.2;
  margin: 0 0 8px;
  color: var(--color-ink);
}

.type-rec-h3 {
  font-family: var(--font-display);
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1.25;
  margin: 0 0 8px;
  color: var(--color-ink);
}

.type-rec-body {
  font-family: var(--font-body);
  font-size: 1rem;
  line-height: 1.6;
  color: var(--color-ink);
  margin: 0 0 8px;
}

.type-rec-caption {
  font-family: var(--font-body);
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.4;
  color: var(--color-muted);
  margin: 0;
}

.btn-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  margin-bottom: 16px;
}

.btn-current-orange {
  background-color: #ff9800;
  color: #fff;
  border: none;
  padding: 10px 20px;
  font-size: 16px;
  font-family: Arial, sans-serif;
  border-radius: 5px;
  cursor: pointer;
}

.btn-current-orange:hover {
  background-color: #e68900;
}

.btn-current-green {
  background-color: #42b983;
  color: #fff;
  text-decoration: none;
  padding: 10px 20px;
  font-size: 16px;
  font-family: Arial, sans-serif;
  border-radius: 5px;
  display: inline-block;
}

.btn-current-green:hover {
  background-color: #36926c;
}

.btn-current-vuetify {
  background-color: #1867c0;
  color: #fff;
  border: none;
  padding: 0 16px;
  height: 36px;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 1.25px;
  text-transform: uppercase;
  border-radius: 4px;
  font-family: Avenir, Helvetica, Arial, sans-serif;
  box-shadow: rgba(0, 0, 0, 0.2) 0px 3px 1px -2px,
    rgba(0, 0, 0, 0.14) 0px 2px 2px 0px, rgba(0, 0, 0, 0.12) 0px 1px 5px 0px;
  cursor: pointer;
}

.btn-current-text {
  background: transparent;
  border: none;
  color: #1867c0;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 1.25px;
  text-transform: uppercase;
  padding: 0 16px;
  height: 36px;
  cursor: pointer;
  font-family: Avenir, Helvetica, Arial, sans-serif;
}

.btn-rec-primary,
.btn-rec-secondary,
.btn-rec-tertiary {
  font-family: var(--font-body);
  font-size: 15px;
  font-weight: 600;
  min-height: 44px;
  padding: 0 20px;
  border-radius: 6px;
  cursor: pointer;
  border: none;
}

.btn-rec-primary {
  background: var(--color-amber);
  color: #fff;
}

.btn-rec-primary:hover {
  background: var(--color-amber-hover);
}

.btn-rec-secondary {
  background: var(--color-forest);
  color: #fff;
}

.btn-rec-secondary:hover {
  background: var(--color-forest-hover);
}

.btn-rec-tertiary {
  background: transparent;
  color: var(--color-forest);
  border: 1px solid var(--color-border);
}

.btn-rec-tertiary:hover {
  background: var(--color-stone);
}

.btn-rec-primary:focus-visible,
.btn-rec-secondary:focus-visible,
.btn-rec-tertiary:focus-visible,
.field-rec:focus {
  outline: 2px solid var(--color-focus);
  outline-offset: 2px;
}

.ds-list {
  margin: 0;
  padding-left: 18px;
  font-size: 14px;
  color: var(--color-muted);
  line-height: 1.5;
}

.field-label-current,
.field-label-rec {
  display: block;
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 6px;
}

.field-current {
  display: block;
  width: 100%;
  max-width: 320px;
  border: 1px solid rgba(0, 0, 0, 0.38);
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 16px;
  font-family: Roboto, sans-serif;
  margin-bottom: 12px;
  box-sizing: border-box;
}

.field-rec {
  display: block;
  width: 100%;
  max-width: 320px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  padding: 12px 14px;
  font-size: 16px;
  font-family: var(--font-body);
  color: var(--color-ink);
  background: var(--color-surface);
  margin-bottom: 12px;
  box-sizing: border-box;
}

.link-current-browser {
  color: #0000ee;
  font-size: 16px;
}

.link-rec {
  color: var(--color-forest);
  font-weight: 500;
  text-underline-offset: 2px;
}

.card-current-event {
  background: #f9f9f9;
  border-left: 5px solid #42b983;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 16px;
  font-family: Arial, sans-serif;
}

.card-current-event h4,
.card-current-vuetify h4,
.card-rec h4 {
  margin: 0 0 8px;
  font-size: 1.1rem;
}

.card-current-vuetify {
  background: #fff;
  border-radius: 4px;
  padding: 16px;
  box-shadow: rgba(0, 0, 0, 0.2) 0px 2px 1px -1px,
    rgba(0, 0, 0, 0.14) 0px 1px 1px 0px, rgba(0, 0, 0, 0.12) 0px 1px 3px 0px;
}

.card-rec {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(26, 31, 28, 0.06);
  padding: 20px;
  margin-bottom: 16px;
}

.card-rec-featured {
  border-left: 4px solid var(--color-forest);
}

.nav-mock-current,
.nav-mock-rec {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  padding: 12px 16px;
  border-radius: 4px;
  margin-bottom: 16px;
  font-size: 14px;
  font-weight: 500;
}

.nav-mock-current {
  background: #fff;
  box-shadow: rgba(0, 0, 0, 0.2) 0px 2px 4px -1px,
    rgba(0, 0, 0, 0.14) 0px 4px 5px 0px, rgba(0, 0, 0, 0.12) 0px 1px 10px 0px;
  font-family: Avenir, Helvetica, Arial, sans-serif;
  letter-spacing: 0.5px;
}

.nav-donate-blue {
  color: #1867c0;
  text-transform: uppercase;
  letter-spacing: 1.25px;
}

.nav-mock-rec {
  background: #fff;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  font-family: var(--font-body);
}

.nav-logo-rec {
  font-family: var(--font-display);
  font-weight: 600;
  color: var(--color-forest);
  margin-right: 8px;
}

.nav-donate-amber {
  background: var(--color-amber);
  color: #fff;
  padding: 8px 14px;
  border-radius: 6px;
  font-weight: 600;
}

.footer-mock-current {
  background: #2c3e50;
  color: #bdc3c7;
  padding: 20px;
  border-radius: 4px;
  font-family: Avenir, Helvetica, Arial, sans-serif;
  font-size: 14px;
}

.footer-mock-current strong {
  color: #ecf0f1;
  display: block;
  margin-bottom: 8px;
}

.footer-mock-rec {
  background: var(--color-slate);
  color: #c9d0cb;
  padding: 20px;
  border-radius: 8px;
  font-family: var(--font-body);
  font-size: 14px;
}

.footer-mock-rec strong {
  color: #fff;
  display: block;
  margin-bottom: 8px;
  font-family: var(--font-display);
}

.ds-footer-note {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid var(--color-border);
  color: var(--color-muted);
  font-size: 14px;
}

.ds-footer-note p {
  margin: 0;
  max-width: 680px;
}
</style>
